import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import {
  Table, Input, InputNumber, Select, DatePicker, Space, Button,
  Tag, Flex, Radio, Checkbox, Divider, Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  FilterOutlined, ReloadOutlined, SearchOutlined,
  CloseOutlined, ColumnHeightOutlined,
} from '@ant-design/icons';
import type { ColumnDef, ColumnDataType, FilterState } from '@shared/types';
import {
  DEFAULT_PAGE_SIZE, DISPLAY_DATE_FORMAT, BASE_CURRENCY_CODE,
} from '@shared/constants';
import { roundMoney } from '@shared/utils';
import dayjs from 'dayjs';

const { Text } = Typography;

// ── Exported Types ──

export interface DataTableProps<T extends Record<string, unknown> = Record<string, unknown>> {
  columns: ColumnDef<T>[];
  dataSource?: T[];
  fetchData?: (params: FetchParams) => Promise<{ data: T[]; total: number }>;
  rowKey?: string | ((record: T) => string);
  loading?: boolean;
  title?: string;
  toolbar?: ReactNode;
  onRow?: TableProps<T>['onRow'];
  rowSelection?: boolean;
  onSelectionChange?: (keys: React.Key[], rows: T[]) => void;
  bulkActions?: ReactNode;
  defaultPageSize?: number;
  size?: 'small' | 'middle' | 'large';
  onRefresh?: () => void;
  scroll?: { x?: number; y?: number };
}

export interface FetchParams {
  page: number;
  pageSize: number;
  filters: FilterState;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
  search?: string;
}

// ── Default Cell Renderers (by data type) ──

function defaultRender(
  value: unknown,
  dataType: ColumnDataType,
  options?: ColumnDef['options'],
): ReactNode {
  if (value === null || value === undefined) {
    return <Text type="secondary">—</Text>;
  }

  switch (dataType) {
    case 'currency':
      return `${BASE_CURRENCY_CODE} ${roundMoney(Number(value)).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

    case 'date':
      return dayjs(value as string).format(DISPLAY_DATE_FORMAT);

    case 'boolean':
      return <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>;

    case 'enum': {
      const opt = options?.find((o) => o.value === value);
      return opt ? <Tag color={opt.color}>{opt.label}</Tag> : String(value);
    }

    case 'tags': {
      const vals = Array.isArray(value) ? value : [value];
      return (
        <Space size={[0, 4]} wrap>
          {vals.map((v) => {
            const opt = options?.find((o) => o.value === v);
            return <Tag key={String(v)} color={opt?.color}>{opt?.label || String(v)}</Tag>;
          })}
        </Space>
      );
    }

    default:
      return String(value);
  }
}

// ── Smart Filter Controls (P9: auto-detects column type) ──

function renderFilterControl(
  col: ColumnDef<never>,
  filterValue: unknown,
  onChange: (value: unknown) => void,
) {
  switch (col.dataType) {
    case 'string':
      return (
        <Input
          placeholder={`Search ${col.label}...`}
          prefix={<SearchOutlined />}
          value={(filterValue as string) || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          allowClear
          style={{ width: 220 }}
        />
      );

    case 'number':
    case 'currency': {
      const range = (filterValue as [number?, number?]) || [undefined, undefined];
      return (
        <Space direction="vertical" size={4}>
          <InputNumber
            placeholder="Min"
            value={range[0]}
            onChange={(v) => {
              const next: [number?, number?] = [v ?? undefined, range[1]];
              onChange(next[0] === undefined && next[1] === undefined ? undefined : next);
            }}
            prefix={col.dataType === 'currency' ? BASE_CURRENCY_CODE : undefined}
            style={{ width: 180 }}
          />
          <InputNumber
            placeholder="Max"
            value={range[1]}
            onChange={(v) => {
              const next: [number?, number?] = [range[0], v ?? undefined];
              onChange(next[0] === undefined && next[1] === undefined ? undefined : next);
            }}
            prefix={col.dataType === 'currency' ? BASE_CURRENCY_CODE : undefined}
            style={{ width: 180 }}
          />
        </Space>
      );
    }

    case 'date':
      return (
        <DatePicker.RangePicker
          value={
            filterValue
              ? [dayjs((filterValue as [string, string])[0]), dayjs((filterValue as [string, string])[1])]
              : undefined
          }
          onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              onChange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
            } else {
              onChange(undefined);
            }
          }}
          style={{ width: 260 }}
        />
      );

    case 'boolean':
      return (
        <Radio.Group
          value={filterValue === undefined ? 'all' : filterValue ? 'yes' : 'no'}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === 'all' ? undefined : v === 'yes');
          }}
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="yes">Yes</Radio.Button>
          <Radio.Button value="no">No</Radio.Button>
        </Radio.Group>
      );

    case 'enum':
    case 'tags':
      return (
        <Checkbox.Group
          value={(filterValue as string[]) || []}
          onChange={(checked) => onChange(checked.length ? checked : undefined)}
          style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflow: 'auto' }}
        >
          {(col.options || []).map((opt) => (
            <Checkbox key={opt.value} value={opt.value}>
              {opt.color ? <Tag color={opt.color}>{opt.label}</Tag> : opt.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      );

    case 'ref':
    case 'user':
      return (
        <Select
          mode="multiple"
          placeholder={`Search ${col.label}...`}
          value={(filterValue as string[]) || []}
          onChange={(v: string[]) => onChange(v.length ? v : undefined)}
          options={col.options}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: 240 }}
          maxTagCount={3}
        />
      );

    default:
      return null;
  }
}

// ── Client-side filter matching ──

function matchesFilter(value: unknown, filter: FilterState[string]): boolean {
  if (filter.value === undefined || filter.value === null) return true;

  switch (filter.type) {
    case 'string':
      return String(value ?? '').toLowerCase().includes(String(filter.value).toLowerCase());

    case 'number':
    case 'currency': {
      const [min, max] = filter.value as [number?, number?];
      const num = Number(value);
      if (min !== undefined && num < min) return false;
      if (max !== undefined && num > max) return false;
      return true;
    }

    case 'date': {
      const [start, end] = filter.value as [string, string];
      const d = dayjs(value as string);
      return d.isAfter(dayjs(start).subtract(1, 'day')) && d.isBefore(dayjs(end).add(1, 'day'));
    }

    case 'boolean':
      return value === filter.value;

    case 'enum':
    case 'tags':
    case 'ref':
    case 'user': {
      const selected = filter.value as string[];
      if (Array.isArray(value)) {
        return value.some((v) => selected.includes(String(v)));
      }
      return selected.includes(String(value));
    }

    default:
      return true;
  }
}

// ── Filter label formatter (for active filter tags) ──

function formatFilterLabel(col: ColumnDef<never>, value: unknown): string {
  switch (col.dataType) {
    case 'string':
      return `"${value}"`;
    case 'number': {
      const [min, max] = value as [number?, number?];
      if (min !== undefined && max !== undefined) return `${min} – ${max}`;
      if (min !== undefined) return `≥ ${min}`;
      return `≤ ${max}`;
    }
    case 'currency': {
      const [min, max] = value as [number?, number?];
      if (min !== undefined && max !== undefined) return `${BASE_CURRENCY_CODE} ${min} – ${max}`;
      if (min !== undefined) return `≥ ${BASE_CURRENCY_CODE} ${min}`;
      return `≤ ${BASE_CURRENCY_CODE} ${max}`;
    }
    case 'date': {
      const [start, end] = value as [string, string];
      return `${dayjs(start).format(DISPLAY_DATE_FORMAT)} – ${dayjs(end).format(DISPLAY_DATE_FORMAT)}`;
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'enum':
    case 'tags':
    case 'ref':
    case 'user': {
      const vals = value as string[];
      return vals
        .map((v) => col.options?.find((o) => o.value === v)?.label || v)
        .join(', ');
    }
    default:
      return String(value);
  }
}

// ── Main DataTable Component ──

export function DataTable<T extends Record<string, unknown> = Record<string, unknown>>({
  columns,
  dataSource,
  fetchData,
  rowKey = 'id',
  loading: externalLoading,
  title,
  toolbar,
  onRow,
  rowSelection: enableSelection,
  onSelectionChange,
  bulkActions,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  size: initialSize = 'middle',
  onRefresh,
  scroll,
}: DataTableProps<T>) {
  const [filters, setFilters] = useState<FilterState>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortField, setSortField] = useState<string>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [size, setSize] = useState(initialSize);
  const [refreshKey, setRefreshKey] = useState(0);

  // Server-side state
  const [serverData, setServerData] = useState<T[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverLoading, setServerLoading] = useState(false);

  const isServerSide = !!fetchData;

  // Stabilize filters for effect dependency
  const filtersJson = JSON.stringify(filters);

  // Filter change handler
  const handleFilterChange = useCallback((key: string, type: ColumnDataType, value: unknown) => {
    setFilters((prev) => {
      if (value === undefined) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { type, value } };
    });
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setSearch('');
    setPage(1);
  }, []);

  // Server-side data fetching
  useEffect(() => {
    if (!fetchData) return;

    const controller = new AbortController();
    const parsedFilters: FilterState = JSON.parse(filtersJson);
    setServerLoading(true);

    fetchData({ page, pageSize, filters: parsedFilters, sortField, sortOrder, search })
      .then((result) => {
        if (!controller.signal.aborted) {
          setServerData(result.data);
          setServerTotal(result.total);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setServerLoading(false);
      });

    return () => controller.abort();
  }, [fetchData, page, pageSize, filtersJson, sortField, sortOrder, search, refreshKey]);

  // Client-side filtering + sorting
  const filteredData = useMemo(() => {
    if (isServerSide) return serverData;

    let result = dataSource || [];

    // Global search (matches any string column)
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const val = row[col.key];
          return val != null && String(val).toLowerCase().includes(searchLower);
        }),
      );
    }

    // Column filters
    const activeFilters = Object.entries(filters);
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([key, filter]) => matchesFilter(row[key], filter)),
      );
    }

    // Sort
    if (sortField && sortOrder) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        let cmp = 0;
        if (aVal == null && bVal == null) cmp = 0;
        else if (aVal == null) cmp = -1;
        else if (bVal == null) cmp = 1;
        else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
        else cmp = String(aVal).localeCompare(String(bVal));
        return sortOrder === 'ascend' ? cmp : -cmp;
      });
    }

    return result;
  }, [isServerSide, serverData, dataSource, search, filters, sortField, sortOrder, columns]);

  const total = isServerSide ? serverTotal : filteredData.length;
  const loading = externalLoading || serverLoading;
  const activeFilterCount = Object.keys(filters).length;

  // Convert ColumnDef[] → Ant Table columns with smart filter dropdowns
  const tableColumns = useMemo(() => {
    return columns.map((col): NonNullable<TableProps<T>['columns']>[number] => ({
      title: col.label,
      dataIndex: col.key,
      key: col.key,
      width: col.width,
      fixed: col.fixed,
      sorter: col.sortable !== false,
      sortOrder: sortField === col.key ? sortOrder : undefined,
      render: (value: unknown, record: T) =>
        col.render ? col.render(value, record) : defaultRender(value, col.dataType, col.options),
      ...(col.filterable !== false && {
        filterDropdown: ({ close }: { close: () => void }) => (
          <div style={{ padding: 12 }}>
            {renderFilterControl(col, filters[col.key]?.value, (v) => handleFilterChange(col.key, col.dataType, v))}
            <Divider style={{ margin: '8px 0' }} />
            <Flex justify="space-between">
              <Button
                size="small"
                onClick={() => { handleFilterChange(col.key, col.dataType, undefined); close(); }}
              >
                Reset
              </Button>
              <Button type="primary" size="small" onClick={close}>OK</Button>
            </Flex>
          </div>
        ),
        filterIcon: () => (
          <FilterOutlined style={{ color: filters[col.key] ? '#000' : undefined }} />
        ),
      }),
    }));
  }, [columns, filters, sortField, sortOrder, handleFilterChange]);

  // Row selection config
  const selectionConfig = enableSelection
    ? {
        selectedRowKeys,
        onChange: (keys: React.Key[], rows: T[]) => {
          setSelectedRowKeys(keys);
          onSelectionChange?.(keys, rows);
        },
      }
    : undefined;

  // Table change handler (sort)
  const handleTableChange: TableProps<T>['onChange'] = (_pagination, _tableFilters, sorter) => {
    if (!Array.isArray(sorter)) {
      setSortField(sorter.field as string | undefined);
      setSortOrder(sorter.order || undefined);
    }
  };

  const sizeOptions = [
    { label: 'Compact', value: 'small' as const },
    { label: 'Default', value: 'middle' as const },
    { label: 'Large', value: 'large' as const },
  ];

  // Client-side pagination slice
  const paginatedData = isServerSide
    ? serverData
    : filteredData.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      {/* Toolbar */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }} wrap="wrap" gap={8}>
        <Flex align="center" gap={8}>
          {title && <Text strong style={{ fontSize: 16 }}>{title}</Text>}
          {enableSelection && selectedRowKeys.length > 0 && (
            <>
              <Tag closable onClose={() => setSelectedRowKeys([])}>
                {selectedRowKeys.length} selected
              </Tag>
              {bulkActions}
            </>
          )}
        </Flex>

        <Flex align="center" gap={8}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            allowClear
            style={{ width: 220 }}
          />

          {activeFilterCount > 0 && (
            <Button icon={<CloseOutlined />} size="small" onClick={clearAllFilters}>
              Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </Button>
          )}

          <Select
            value={size}
            onChange={setSize}
            options={sizeOptions}
            style={{ width: 110 }}
            suffixIcon={<ColumnHeightOutlined />}
          />

          {(onRefresh || isServerSide) && (
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                onRefresh?.();
                setRefreshKey((k) => k + 1);
              }}
            />
          )}

          {toolbar}
        </Flex>
      </Flex>

      {/* Active filter tags */}
      {activeFilterCount > 0 && (
        <Flex gap={4} wrap="wrap" style={{ marginBottom: 8 }}>
          {Object.entries(filters).map(([key, filter]) => {
            const col = columns.find((c) => c.key === key);
            if (!col) return null;
            return (
              <Tag
                key={key}
                closable
                onClose={() => handleFilterChange(key, filter.type, undefined)}
              >
                <Text strong style={{ fontSize: 12 }}>{col.label}:</Text>{' '}
                <Text style={{ fontSize: 12 }}>{formatFilterLabel(col, filter.value)}</Text>
              </Tag>
            );
          })}
        </Flex>
      )}

      {/* Table */}
      <Table<T>
        columns={tableColumns}
        dataSource={paginatedData}
        rowKey={rowKey}
        loading={loading}
        size={size}
        onRow={onRow}
        rowSelection={selectionConfig}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        scroll={scroll}
      />
    </div>
  );
}
